import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FoodShell from "../../layouts/FoodShell";
import { appModules } from "../../components/global";
import { POST, alert, loading } from "../../components/CustomComponent";
import { decisionQuestions } from "./mockData";
import { Panel, ReasonList, FoodCard, FeedbackBar, Chip, currentMealKey } from "./FoodUi";

// AI Meal Decision: ถาม 3 ข้อ แล้ว POST v1/food/decide ให้ backend เลือกพร้อมเหตุผล (+ คำอธิบายจาก LLM ถ้าเปิดไว้)
export default function FoodDecide() {
  const { title, description } = appModules["food_decide"];
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [thinking, setThinking] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [liked, setLiked] = useState(null);
  const [saved, setSaved] = useState(false);

  const question = decisionQuestions[step];

  const ask = async (nextAnswers, nextExcluded = []) => {
    setThinking(true);
    const result = await POST("v1/food/decide", { ...nextAnswers, ...(nextExcluded.length ? { exclude: nextExcluded.join(",") } : {}) });
    setThinking(false);
    if (result?.status === true) {
      setResult(result.data);
      setExcluded(nextExcluded);
      setLiked(null);
      setSaved(false);
    }
  };

  const choose = (value) => {
    const next = { ...answers, [question.key]: value };
    setAnswers(next);
    if (step < decisionQuestions.length - 1) {
      setStep(step + 1);
      return;
    }
    ask(next);
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setExcluded([]);
  };

  const context = { meal: currentMealKey(), source: "decide" };
  const sendFeedback = (action) => result && POST("v1/food/feedback", { food_uid: result.food.uid, action, context }, null, { silent: true });

  const another = () => {
    if (!result) return;
    sendFeedback("skip");
    ask(answers, [...excluded, result.food.uid]);
  };

  const eatThis = async () => {
    loading(true);
    const saved = await POST("v1/food/meal-logs", { food_uid: result.food.uid, meal: currentMealKey(), source: "decide" });
    loading(false);
    if (saved?.status === true) {
      await alert("", "success", "บันทึกแล้ว", `เพิ่ม "${result.food.name}" ในบันทึกวันนี้แล้ว`);
      navigate("/food/diary");
    }
  };

  const answerLabel = (key) => {
    const q = decisionQuestions.find((item) => item.key === key);
    const option = q?.options.find((item) => item.value === answers[key]);
    return option ? `${option.emoji} ${option.label}` : "";
  };

  return (
    <FoodShell title={title} description={description}>
      <div className="row g-3 justify-content-center">
        <div className="col-xl-8">
          <Panel
            eyebrow="AI Meal Decision"
            icon="fa-solid fa-wand-magic-sparkles"
            title="ให้ AI เลือกให้"
            desc="ไม่ต้องคิดเอง ตอบแค่ 3 ข้อ ระบบจะเอาโปรไฟล์ สิ่งที่กินไปแล้ววันนี้ และสิ่งที่เรียนรู้มาประกอบการตัดสินใจ"
            actions={result || step > 0 ? <button type="button" className="btn stg-btn stg-btn--ghost" onClick={restart}><i className="fa-solid fa-rotate-left" />เริ่มใหม่</button> : null}
          >
            <div className="food-steps mb-3">
              {decisionQuestions.map((item, index) => {
                const done = answers[item.key] !== undefined;
                const current = !result && !thinking && index === step;
                return (
                  <button key={item.key} type="button" className={`food-step ${done ? "is-done" : ""} ${current ? "is-current" : ""}`} onClick={() => { if (done && !thinking) { setResult(null); setStep(index); } }} disabled={!done && !current}>
                    <span className="food-step-no">{done ? <i className="fa-solid fa-check" /> : index + 1}</span>
                    <span className="food-step-text">
                      <small>{item.title}</small>
                      <strong>{done ? answerLabel(item.key) : current ? "กำลังตอบ" : "รอ"}</strong>
                    </span>
                  </button>
                );
              })}
            </div>

            {thinking ? (
              <div className="food-thinking">
                <span className="food-thinking-orb"><i className="fa-solid fa-robot" /></span>
                <div className="food-thinking-title">AI กำลังเลือกให้...</div>
                <div className="food-thinking-text">ดูเป้าหมาย · พลังงานที่เหลือวันนี้ · มื้อก่อนหน้า · งบที่เลือก · สิ่งที่คุณเคยกดชอบ</div>
              </div>
            ) : result ? (
              <div className="food-result">
                <div className="food-result-head">
                  <span className="food-pick-badge"><i className="fa-solid fa-robot" />วันนี้ผมเลือก</span>
                </div>
                <div className="food-result-hero">
                  <span className="food-result-emoji">{result.food.emoji}</span>
                  <h2 className="food-result-name">“{result.food.name}”</h2>
                  <div className="food-pick-stats justify-content-center">
                    <span><strong>{result.food.kcal}</strong> kcal</span>
                    <span>Protein <strong>{result.food.protein}</strong> g</span>
                    <span>ประมาณ <strong>฿{result.food.price}</strong></span>
                  </div>
                  <div className="mt-2 d-flex flex-wrap gap-1 justify-content-center">
                    {result.food.tags.map((tag) => <Chip key={tag} tone="orange">{tag}</Chip>)}
                  </div>
                  {result.summary ? <p className="food-tip mt-3 text-start"><i className="fa-solid fa-robot" />{result.summary}</p> : null}
                </div>
                <div className="food-pick-reasons">
                  <div className="food-pick-reasons-title">เหตุผล</div>
                  <ReasonList reasons={result.reasons} />
                </div>
                <div className="food-pick-actions justify-content-center">
                  <button type="button" className="btn stg-btn stg-btn--primary" onClick={eatThis}><i className="fa-solid fa-check" />กินอันนี้</button>
                  <button type="button" className="btn stg-btn stg-btn--ghost" onClick={another}><i className="fa-solid fa-rotate" />ขออีก</button>
                </div>
                <FeedbackBar liked={liked} saved={saved} onLike={() => { setLiked(true); sendFeedback("like"); }} onDislike={() => { setLiked(false); sendFeedback("dislike"); another(); }} onAnother={another} onSave={() => { const next = !saved; setSaved(next); sendFeedback(next ? "save" : "unsave"); }} />

                {result.alternatives?.length ? (
                  <div className="mt-4">
                    <div className="food-subhead">ตัวเลือกรองที่ผ่านเงื่อนไขเดียวกัน</div>
                    <div className="d-grid gap-2">
                      {result.alternatives.map((item) => (
                        <FoodCard key={item.food.uid} food={item.food} onClick={() => { setResult({ ...result, food: item.food, reasons: item.reasons, summary: null, alternatives: [{ food: result.food, reasons: result.reasons }, ...result.alternatives.filter((alt) => alt.food.uid !== item.food.uid)] }); setLiked(null); setSaved(false); }} />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="food-question">
                <p className="food-question-no">คำถามที่ {step + 1} / {decisionQuestions.length}</p>
                <h2 className="food-question-title">{question.title}</h2>
                <p className="food-question-hint">{question.hint}</p>
                <div className="food-options">
                  {question.options.map((option) => (
                    <button key={option.value} type="button" className={`food-option ${answers[question.key] === option.value ? "is-active" : ""}`} onClick={() => choose(option.value)}>
                      <span className="food-option-emoji">{option.emoji}</span>
                      <span className="food-option-label">{option.label}</span>
                      {option.hint ? <span className="food-option-hint">{option.hint}</span> : null}
                    </button>
                  ))}
                </div>
                {step > 0 ? (
                  <button type="button" className="btn stg-btn stg-btn--ghost stg-btn--sm mt-3" onClick={() => setStep(step - 1)}><i className="fa-solid fa-arrow-left" />ย้อนกลับ</button>
                ) : null}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </FoodShell>
  );
}
